import { faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';
import { useBooth } from '../../../../../../hooks/useBooth';
import { Booth } from '../../../../../../interfaces/interfaces';
import { VyUploadCrop } from '../../../../../common/VyUploadCrop';
import BoothHeaderTextEdit from './BoothHeaderTextEdit';
import { VyAspectRatioHint } from '../../../../../common/VyAspectRatioHint';

interface HeaderEditInterface {
  booth: Booth;
  hideContactShareButton?: boolean;
  hideLogo?: boolean;
  showPreview?: boolean;
  update: (path: string, value?: string | boolean) => Promise<void>;
  isSpotlight?: boolean;
}

const HeaderEdit = ({ booth, hideContactShareButton, hideLogo, showPreview = false, update, isSpotlight }: HeaderEditInterface) => {
  const { uploadLogo, updateProp, uploadBackgroundImage, deleteBackground } = useBooth();

  const { t } = useTranslation();

  const backgroundImageType = isSpotlight ? 'spotlightBackgroundImage' : 'backgroundImage';

  async function _uploadLogo(file: Blob) {
    await uploadLogo(file, booth._id);
    toast.success(t('feedback:changes-saved'));
  }

  const _uploadBackgroundImage = async (image: Blob) => {
    try {
      await uploadBackgroundImage(image, backgroundImageType, booth?._id!);
    } catch (error) {
      console.error(error);
    }
  };

  const _deleteBackgroundImage = async () => {
    try {
      await deleteBackground(booth, backgroundImageType);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className={`w-full flex gap-8 flex-grow ${!showPreview ? 'md:items-center flex-col md:flex-row' : 'md:items-start flex-col'}`}>
      <BoothHeaderTextEdit booth={booth} update={update} />
      {!hideLogo && (
        <>
          <div
            className="rounded-sm outline-none cursor-pointer focus:ring-2 ring-offset-4 ring-vystemblue"
            onClick={() => booth.logo && updateProp(booth._id, 'logo', null)}
            tabIndex={0}
            id="logo-focus">
            {!booth.logo ? (
              <VyAspectRatioHint asTooltip aspectRatio="300 x 300">
                <VyUploadCrop cropSize={{ width: 400, height: 400 }} onImage={_uploadLogo}>
                  <FontAwesomeIcon icon={faUpload} className="mr-2" />
                  <span>{t('booth:upload-booth-logo')}</span>
                </VyUploadCrop>
              </VyAspectRatioHint>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                <span>{t('booth:delete-booth-logo')}</span>
              </>
            )}
          </div>
          <div
            className="rounded-sm outline-none cursor-pointer focus:ring-2 ring-offset-4 ring-vystemblue"
            onClick={() => booth[backgroundImageType] && _deleteBackgroundImage()}
            tabIndex={0}
            id="backgroundImage-focus">
            {!booth[backgroundImageType] ? (
              <VyAspectRatioHint asTooltip aspectRatio="512 x 384">
                <VyUploadCrop cropSize={{ width: isSpotlight ? 976 : 256, height: 192 }} onImage={_uploadBackgroundImage}>
                  <FontAwesomeIcon icon={faUpload} className="mr-2" />
                  <span>{t('booth:upload-booth-preview')}</span>
                </VyUploadCrop>
              </VyAspectRatioHint>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                <span>{t('booth:delete-booth-preview')}</span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default HeaderEdit;
